/**
 * Import GeoServer Modal
 *
 * Dialog for importing layers from GeoServer GetCapabilities.
 * Part of Layer Drawer 2.0 - Phase 3 (Admin UI).
 *
 * Features:
 * - Fetch GetCapabilities from GeoServer
 * - Display available layers in selectable list
 * - Auto-fill configuration from metadata
 * - Batch import support
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFile, faLevelDownAlt, faThumbtack, faChevronRight, faChevronDown, faRotate } from '@fortawesome/free-solid-svg-icons';
import { LayerService } from '../../../../services/layerService';
import { CreateLayerInput, LayerCategory, LayerType } from '../../../../types/layer';
import './ImportGeoServerModal.scss';
import { useMessages } from '../../../common/AppMessages';

interface ImportGeoServerModalProps {
  onClose: (success: boolean) => void;
}

interface GeoServerLayerInfo {
  name: string;
  title: string;
  abstract?: string;
  workspace?: string;
  layerName?: string;
  styles: Array<{
    name: string;
    title: string;
    legendUrl?: string;
  }>;
  boundingBox?: {
    minx: number;
    miny: number;
    maxx: number;
    maxy: number;
    crs: string;
  };
  geographicBounds?: {
    minLat: number;
    minLon: number;
    maxLat: number;
    maxLon: number;
  };
  crs: string[];
  resourceType?: 'featureType' | 'coverage' | 'unknown';
  geometryType?: 'Point' | 'LineString' | 'Polygon' | 'MultiPoint' | 'MultiLineString' | 'MultiPolygon' | 'GeometryCollection';
  availableTypes?: string[];
  isGroup?: boolean;
  queryable?: boolean;
  parentGroup?: string | null;
  childLayers?: string[];
  existingUsage?: any[];
}

export const ImportGeoServerModal: React.FC<ImportGeoServerModalProps> = ({ onClose }) => {
  const { showMessage } = useMessages();
  const layerService = new LayerService();

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [layers, setLayers] = useState<GeoServerLayerInfo[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<Set<string>>(new Set());
  const [selectedStyles, setSelectedStyles] = useState<Map<string, string>>(new Map());
  const [selectedImportTypes, setSelectedImportTypes] = useState<Map<string, LayerType>>(new Map());
  const [selectedWFSStyles, setSelectedWFSStyles] = useState<Map<string, any>>(new Map());
  const [selectedRefreshIntervals, setSelectedRefreshIntervals] = useState<Map<string, number>>(new Map());
  const [selectedCQLFilters, setSelectedCQLFilters] = useState<Map<string, string>>(new Map());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [importCategory, setImportCategory] = useState<LayerCategory>('overlays');
  const [importGroup, setImportGroup] = useState('');
  const [existingGroups, setExistingGroups] = useState<string[]>([]);
  const [useCustomGroup, setUseCustomGroup] = useState(false);
  const [customGroupName, setCustomGroupName] = useState('');

  /**
   * Fetch existing layer groups from database
   */
  useEffect(() => {
    fetchExistingGroups();
  }, []);

  const fetchExistingGroups = async () => {
    try {
      const allLayers = await layerService.getAllLayers();
      // Extract unique non-null layer groups
      const groups = [...new Set(
        allLayers
          .map(layer => layer.layer_group)
          .filter((group): group is string => group !== null && group !== '')
      )].sort();
      setExistingGroups(groups);
    } catch (err) {
      console.warn('[ImportGeoServer] Failed to fetch existing groups:', err);
      // Non-critical error, continue without existing groups
    }
  };

  /**
   * Fetch layers from GeoServer
   */
  useEffect(() => {
    fetchLayers();
  }, []);

  const fetchLayers = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch both GeoServer capabilities and existing database layers
      const [capabilities, existingLayers] = await Promise.all([
        layerService.getGeoServerCapabilities(),
        layerService.getAllLayers(),
      ]);

      console.log('[ImportGeoServer] Fetched capabilities:', capabilities);
      console.log('[ImportGeoServer] Existing layers:', existingLayers);

      const fetchedLayers = capabilities.layers || [];

      // Map existing layers by config.layers for quick lookup
      const existingBySourceLayer = new Map<string, any[]>();
      existingLayers.forEach((layer: any) => {
        // Check config.layers field for WMS layers (or typeName for WFS)
        const sourceLayerName = layer.config?.layers || layer.config?.typeName;
        if (sourceLayerName) {
          const existing = existingBySourceLayer.get(sourceLayerName) || [];
          existing.push(layer);
          existingBySourceLayer.set(sourceLayerName, existing);
        }
      });

      console.log('[ImportGeoServer] Mapped existing layers:', existingBySourceLayer);

      // Attach existing usage info to each GeoServer layer
      fetchedLayers.forEach((layer: any) => {
        const existing = existingBySourceLayer.get(layer.name) || [];
        layer.existingUsage = existing;
        if (existing.length > 0) {
          console.log(`[ImportGeoServer] Layer ${layer.name} has ${existing.length} existing uses`);
        }
      });

      setLayers(fetchedLayers);

      // Initialize default styles for each layer (first style or 'default')
      const defaultStyles = new Map<string, string>();
      fetchedLayers.forEach((layer: any) => {
        const defaultStyle = layer.styles[0]?.name || 'default';
        defaultStyles.set(layer.name, defaultStyle);
      });
      setSelectedStyles(defaultStyles);

      // Initialize default import types for each layer
      const defaultImportTypes = new Map<string, LayerType>();
      fetchedLayers.forEach((layer: any) => {
        // Default to WFS for featureType layers, WMS for everything else
        if (layer.resourceType === 'featureType') {
          defaultImportTypes.set(layer.name, 'wfs');
        } else {
          defaultImportTypes.set(layer.name, 'wms');
        }
      });
      setSelectedImportTypes(defaultImportTypes);

      // Initialize default WFS styles for each layer
      const defaultWFSStyles = new Map<string, any>();
      fetchedLayers.forEach((layer: any) => {
        const defaultStyle = getDefaultWFSStyle(layer.geometryType);
        defaultWFSStyles.set(layer.name, defaultStyle);
      });
      setSelectedWFSStyles(defaultWFSStyles);

      // Initialize default refresh intervals (5 minutes)
      const defaultRefreshIntervals = new Map<string, number>();
      fetchedLayers.forEach((layer: any) => {
        defaultRefreshIntervals.set(layer.name, 5); // 5 minutes default
      });
      setSelectedRefreshIntervals(defaultRefreshIntervals);
    } catch (err: any) {
      console.error('[ImportGeoServer] Error fetching capabilities:', err);
      setError(err.message || 'Failed to fetch GeoServer layers');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle layer selection
   */
  const toggleLayerSelection = (layerName: string) => {
    const newSelection = new Set(selectedLayers);
    if (newSelection.has(layerName)) {
      newSelection.delete(layerName);
    } else {
      newSelection.add(layerName);
    }
    setSelectedLayers(newSelection);
  };

  /**
   * Select all visible layers
   */
  const selectAllVisible = () => {
    const visibleLayers = filteredLayers.map((layer) => layer.name);
    setSelectedLayers(new Set(visibleLayers));
  };

  /**
   * Deselect all layers
   */
  const deselectAll = () => {
    setSelectedLayers(new Set());
  };

  /**
   * Update selected style for a layer
   */
  const updateLayerStyle = (layerName: string, styleName: string) => {
    setSelectedStyles(prev => {
      const updated = new Map(prev);
      updated.set(layerName, styleName);
      return updated;
    });
  };

  /**
   * Update selected import type for a layer
   */
  const updateLayerImportType = (layerName: string, importType: LayerType) => {
    setSelectedImportTypes(prev => {
      const updated = new Map(prev);
      updated.set(layerName, importType);
      return updated;
    });
  };

  /**
   * Update WFS style for a layer
   */
  const updateWFSStyle = (layerName: string, styleUpdates: any) => {
    setSelectedWFSStyles(prev => {
      const updated = new Map(prev);
      const currentStyle = updated.get(layerName) || { type: 'circle', paint: {} };
      updated.set(layerName, {
        ...currentStyle,
        paint: { ...currentStyle.paint, ...styleUpdates },
      });
      return updated;
    });
  };

  /**
   * Update WFS style type for a layer
   */
  const updateWFSStyleType = (layerName: string, newType: string, geometryType?: string) => {
    setSelectedWFSStyles(prev => {
      const updated = new Map(prev);
      // Get default paint for new type
      const defaultStyle = getDefaultWFSStyle(geometryType);
      if (newType !== defaultStyle.type) {
        // If changing to a different type, get defaults for that type
        let defaultPaint = {};
        if (newType === 'circle') {
          defaultPaint = {
            'circle-radius': 6,
            'circle-color': '#3b82f6',
            'circle-opacity': 0.8,
            'circle-stroke-width': 2,
            'circle-stroke-color': '#ffffff',
          };
        } else if (newType === 'line') {
          defaultPaint = {
            'line-color': '#3b82f6',
            'line-width': 2,
            'line-opacity': 0.8,
          };
        } else if (newType === 'fill') {
          defaultPaint = {
            'fill-color': '#3b82f6',
            'fill-opacity': 0.5,
            'fill-outline-color': '#1e40af',
          };
        }
        updated.set(layerName, { type: newType, paint: defaultPaint });
      } else {
        updated.set(layerName, defaultStyle);
      }
      return updated;
    });
  };

  /**
   * Update refresh interval for a layer
   */
  const updateRefreshInterval = (layerName: string, minutes: number) => {
    setSelectedRefreshIntervals(prev => {
      const updated = new Map(prev);
      updated.set(layerName, minutes);
      return updated;
    });
  };

  /**
   * Update CQL filter for a layer
   */
  const updateCQLFilter = (layerName: string, cqlFilter: string) => {
    setSelectedCQLFilters(prev => {
      const updated = new Map(prev);
      updated.set(layerName, cqlFilter);
      return updated;
    });
  };

  /**
   * Toggle layer group expansion
   */
  const toggleGroupExpansion = (groupName: string) => {
    setExpandedGroups(prev => {
      const updated = new Set(prev);
      if (updated.has(groupName)) {
        updated.delete(groupName);
      } else {
        updated.add(groupName);
      }
      return updated;
    });
  };

  /**
   * Generate default WFS style config based on geometry type
   */
  const getDefaultWFSStyle = (geometryType?: string) => {
    if (!geometryType) {
      // Default to circle if geometry type unknown
      return {
        type: 'circle',
        paint: {
          'circle-radius': 6,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      };
    }

    // Point geometries
    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
      return {
        type: 'circle',
        paint: {
          'circle-radius': 6,
          'circle-color': '#3b82f6',
          'circle-opacity': 0.8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff'
        }
      };
    }

    // Line geometries
    if (geometryType === 'LineString' || geometryType === 'MultiLineString') {
      return {
        type: 'line',
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.8
        }
      };
    }

    // Polygon geometries
    if (geometryType === 'Polygon' || geometryType === 'MultiPolygon') {
      return {
        type: 'fill',
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.5,
          'fill-outline-color': '#1e40af'
        }
      };
    }

    // GeometryCollection - default to circle
    return {
      type: 'circle',
      paint: {
        'circle-radius': 6,
        'circle-color': '#3b82f6',
        'circle-opacity': 0.8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff'
      }
    };
  };

  /**
   * Import selected layers
   */
  const handleImport = async () => {
    if (selectedLayers.size === 0) {
      showMessage('Please select at least one layer to import', 'warning');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const selectedLayerObjects = layers.filter((layer) => selectedLayers.has(layer.name));

      // Get GeoServer base URL from backend proxy
      const geoserverBaseUrl = '/api/geoserver/geoserver';

      // Determine the group to use
      const groupToUse = useCustomGroup ? customGroupName : importGroup;

      // Import each selected layer
      for (const layer of selectedLayerObjects) {
        // Get the selected import type for this layer
        const importType = selectedImportTypes.get(layer.name) || 'wms';

        let layerInput: CreateLayerInput;

        if (importType === 'wfs') {
          // ===== WFS Import =====
          // Construct WFS source URL
          const sourceUrl = `${geoserverBaseUrl}/wfs`;

          // Get custom style for this layer (or use default)
          const customStyle = selectedWFSStyles.get(layer.name) || getDefaultWFSStyle(layer.geometryType);

          // Get custom refresh interval for this layer (or use default 5 minutes)
          const refreshMinutes = selectedRefreshIntervals.get(layer.name) || 5;
          const refreshInterval = refreshMinutes > 0 ? refreshMinutes * 60000 : undefined;

          // Get custom CQL filter for this layer (if any)
          const cqlFilter = selectedCQLFilters.get(layer.name) || undefined;

          layerInput = {
            name: layer.title || layer.name,
            type: 'wfs',
            category: importCategory,
            layer_group: groupToUse || layer.workspace || null,
            tags: layer.workspace ? [layer.workspace] : [],
            source_url: sourceUrl,
            legend_url: null, // WFS layers don't have legend URLs
            zoom_min: 0,
            zoom_max: 22,
            default_opacity: 1.0,
            default_order: 0,
            enabled: false, // Import as disabled by default
            config: {
              typeName: layer.name,
              version: '2.0.0',
              outputFormat: 'application/json',
              maxFeatures: 10000,
              srsName: 'EPSG:4326',
              style: customStyle,
              refreshInterval: refreshInterval,
              cqlFilter: cqlFilter,
            },
            metadata: {
              abstract: layer.abstract,
              boundingBox: layer.boundingBox,
              geographicBounds: layer.geographicBounds,
              crs: layer.crs,
              imported: true,
              importedAt: new Date().toISOString(),
              geoserverParentGroup: layer.parentGroup || null,
              isGeoServerGroup: layer.isGroup || false,
              resourceType: layer.resourceType,
              geometryType: layer.geometryType,
            },
          };
        } else {
          // ===== WMS Import (default) =====
          // Get the selected style for this layer
          const styleName = selectedStyles.get(layer.name) || layer.styles[0]?.name || 'default';

          // Construct WMS source URL
          const sourceUrl = `${geoserverBaseUrl}/wms`;

          // Construct legend URL
          const legendUrl = `${geoserverBaseUrl}/wms?service=WMS&version=1.1.1&request=GetLegendGraphic&layer=${encodeURIComponent(layer.name)}&style=${encodeURIComponent(styleName)}&format=image/png`;

          layerInput = {
            name: layer.title || layer.name,
            type: 'wms',
            category: importCategory,
            layer_group: groupToUse || layer.workspace || null,
            tags: layer.workspace ? [layer.workspace] : [],
            source_url: sourceUrl,
            legend_url: legendUrl,
            zoom_min: 0,
            zoom_max: 22,
            default_opacity: 1.0,
            default_order: 0,
            enabled: false, // Import as disabled by default
            config: {
              url: sourceUrl,
              layers: layer.name,
              styles: styleName,
              format: 'image/png',
              transparent: true,
              version: '1.3.0',
              crs: 'EPSG:3857', // Always use Web Mercator for Mapbox compatibility
            },
            metadata: {
              abstract: layer.abstract,
              boundingBox: layer.boundingBox,
              geographicBounds: layer.geographicBounds,
              availableStyles: layer.styles.map((s) => s.name),
              crs: layer.crs,
              imported: true,
              importedAt: new Date().toISOString(),
              geoserverParentGroup: layer.parentGroup || null,
              isGeoServerGroup: layer.isGroup || false,
              resourceType: layer.resourceType,
              geometryType: layer.geometryType,
            },
          };
        }

        await layerService.createLayer(layerInput);
      }

      // Show success message
      setSuccessMessage(`Successfully imported ${selectedLayers.size} layer(s)`);

      // Close modal after short delay to show success message
      setTimeout(() => {
        onClose(true);
      }, 1500);
    } catch (err: any) {
      console.error('[ImportGeoServer] Error importing layers:', err);
      setError(err.message || 'Failed to import layers');
    } finally {
      setImporting(false);
    }
  };

  /**
   * Filter layers by text
   */
  const filteredLayers = layers.filter(
    (layer) =>
      filterText === '' ||
      layer.name.toLowerCase().includes(filterText.toLowerCase()) ||
      layer.title?.toLowerCase().includes(filterText.toLowerCase()) ||
      layer.workspace?.toLowerCase().includes(filterText.toLowerCase())
  );

  /**
   * Render a layer item (can be a group or individual layer)
   */
  const renderLayerItem = (layer: any) => {
    const isGroup = layer.isGroup;
    const isExpanded = expandedGroups.has(layer.name);

    // Get child layers from the layer.childLayers array (fetched from REST API)
    const childLayers = isGroup && layer.childLayers && layer.childLayers.length > 0
      ? layer.childLayers
        .map((childName: string) => layers.find(l => l.name === childName))
        .filter(Boolean) // Remove any undefined entries
      : [];

    if (isGroup) {
      // Render layer group
      return (
        <div key={layer.name} className="layer-group-item">
          <div
            className={`layer-group-header ${selectedLayers.has(layer.name) ? 'selected' : ''}`}
          >
            <input
              type="checkbox"
              checked={selectedLayers.has(layer.name)}
              onChange={() => toggleLayerSelection(layer.name)}
              onClick={(e) => e.stopPropagation()}
            />
            <span
              className="layer-group-expand-icon"
              onClick={() => toggleGroupExpansion(layer.name)}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} />
            </span>
            <span className="layer-group-indicator"><FontAwesomeIcon icon={faFolder} /></span>
            <div className="layer-info" onClick={() => toggleGroupExpansion(layer.name)}>
              <div className="layer-name">
                {layer.title || layer.name}
                <span className="layer-group-badge">Group ({childLayers.length})</span>
              </div>
              <div className="layer-details">
                <span className="layer-workspace">{layer.workspace || 'Default'}</span>
                <span className="layer-technical-name">{layer.name}</span>
              </div>
              {layer.abstract && (
                <div className="layer-abstract">{layer.abstract}</div>
              )}
              {layer.existingUsage && layer.existingUsage.length > 0 && (
                <div className="layer-existing-usage">
                  <div className="usage-header"><FontAwesomeIcon icon={faThumbtack} /> Already in use:</div>
                  <ul className="usage-list">
                    {layer.existingUsage.map((existing: any) => (
                      <li key={existing._id}>
                        {existing.name} - {existing.layer_group || 'No group'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          {isExpanded && childLayers.length > 0 && (
            <div className="layer-group-children">
              {childLayers.map((child: any) => (
                <div key={child.name} className="layer-child-info">
                  <span className="layer-child-indicator"><FontAwesomeIcon icon={faLevelDownAlt} /></span>
                  <div className="layer-info">
                    <div className="layer-name">{child.title || child.name}</div>
                    <div className="layer-details">
                      <span className="layer-technical-name">{child.name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    } else {
      // Render individual layer
      const isChild = layer.parentGroup !== null;
      return (
        <div
          key={layer.name}
          className={`layer-item ${isChild ? 'layer-item-child' : ''} ${selectedLayers.has(layer.name) ? 'selected' : ''
            } ${!layer.queryable ? 'non-queryable' : ''}`}
          onClick={() => layer.queryable && toggleLayerSelection(layer.name)}
        >
          <input
            type="checkbox"
            checked={selectedLayers.has(layer.name)}
            onChange={() => toggleLayerSelection(layer.name)}
            onClick={(e) => e.stopPropagation()}
            disabled={!layer.queryable}
          />
          <span className="layer-type-indicator"><FontAwesomeIcon icon={faFile} /></span>
          <div className="layer-info">
            <div className="layer-name">
              {layer.title || layer.name}
              {!layer.queryable && <span className="layer-non-queryable-badge">Non-queryable</span>}
            </div>
            <div className="layer-details">
              <span className="layer-workspace">{layer.workspace || 'Default'}</span>
              <span className="layer-technical-name">{layer.name}</span>
            </div>
            {layer.abstract && (
              <div className="layer-abstract">{layer.abstract}</div>
            )}
            {layer.existingUsage && layer.existingUsage.length > 0 && (
              <div className="layer-existing-usage">
                <div className="usage-header"><FontAwesomeIcon icon={faThumbtack} /> Already in use:</div>
                <ul className="usage-list">
                  {layer.existingUsage.map((existing: any) => (
                    <li key={existing._id}>
                      {existing.name} - {existing.layer_group || 'No group'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Import Type Selector (for layers that support multiple types) */}
            {layer.availableTypes && layer.availableTypes.length > 1 && (
              <div className="layer-import-type">
                <label htmlFor={`import-type-${layer.name}`}>Import as: </label>
                <select
                  id={`import-type-${layer.name}`}
                  value={selectedImportTypes.get(layer.name) || 'wms'}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateLayerImportType(layer.name, e.target.value as LayerType);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {layer.availableTypes.map((type: string) => (
                    <option key={type} value={type}>
                      {type.toUpperCase()} {type === 'wms' ? '(Raster)' : '(Vector/GeoJSON)'}
                    </option>
                  ))}
                </select>
                {layer.geometryType && selectedImportTypes.get(layer.name) === 'wfs' && (
                  <span className="layer-geometry-hint">
                    ({layer.geometryType})
                  </span>
                )}
              </div>
            )}
            {/* WFS Style Customization (only shown when WFS is selected) */}
            {selectedImportTypes.get(layer.name) === 'wfs' && selectedLayers.has(layer.name) && (
              <div className="wfs-style-config" onClick={(e) => e.stopPropagation()}>
                <div className="style-config-header">
                  <strong>Style Settings:</strong>
                </div>
                {(() => {
                  const wfsStyle = selectedWFSStyles.get(layer.name) || { type: 'circle', paint: {} };
                  const styleType = wfsStyle.type || 'circle';
                  const paint = wfsStyle.paint || {};

                  return (
                    <>
                      <div className="form-group-inline">
                        <label htmlFor={`style-type-${layer.name}`}>Geometry: </label>
                        <select
                          id={`style-type-${layer.name}`}
                          value={styleType}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateWFSStyleType(layer.name, e.target.value, layer.geometryType);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="circle">Point (Circle)</option>
                          <option value="line">Line</option>
                          <option value="fill">Polygon (Fill)</option>
                        </select>
                      </div>

                      {/* Point/Circle Styles */}
                      {styleType === 'circle' && (
                        <div className="style-controls">
                          <div className="form-group-inline">
                            <label>Fill: </label>
                            <input
                              type="color"
                              value={paint['circle-color'] || '#3b82f6'}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'circle-color': e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <label style={{ marginLeft: '8px' }}>Stroke: </label>
                            <input
                              type="color"
                              value={paint['circle-stroke-color'] || '#ffffff'}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'circle-stroke-color': e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="form-group-inline">
                            <label>Radius: {paint['circle-radius'] || 6}px</label>
                            <input
                              type="range"
                              min="1"
                              max="20"
                              value={paint['circle-radius'] || 6}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'circle-radius': parseInt(e.target.value) });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ flex: 1, marginLeft: '8px' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Line Styles */}
                      {styleType === 'line' && (
                        <div className="style-controls">
                          <div className="form-group-inline">
                            <label>Color: </label>
                            <input
                              type="color"
                              value={paint['line-color'] || '#3b82f6'}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'line-color': e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="form-group-inline">
                            <label>Width: {paint['line-width'] || 2}px</label>
                            <input
                              type="range"
                              min="1"
                              max="10"
                              value={paint['line-width'] || 2}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'line-width': parseInt(e.target.value) });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ flex: 1, marginLeft: '8px' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Polygon/Fill Styles */}
                      {styleType === 'fill' && (
                        <div className="style-controls">
                          <div className="form-group-inline">
                            <label>Fill: </label>
                            <input
                              type="color"
                              value={paint['fill-color'] || '#3b82f6'}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'fill-color': e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <label style={{ marginLeft: '8px' }}>Outline: </label>
                            <input
                              type="color"
                              value={paint['fill-outline-color'] || '#1e40af'}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'fill-outline-color': e.target.value });
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="form-group-inline">
                            <label>Opacity: {((paint['fill-opacity'] || 0.5) * 100).toFixed(0)}%</label>
                            <input
                              type="range"
                              min="0"
                              max="1"
                              step="0.05"
                              value={paint['fill-opacity'] || 0.5}
                              onChange={(e) => {
                                e.stopPropagation();
                                updateWFSStyle(layer.name, { 'fill-opacity': parseFloat(e.target.value) });
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{ flex: 1, marginLeft: '8px' }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Refresh Interval */}
                      <div className="form-group-inline">
                        <label htmlFor={`refresh-${layer.name}`}>Refresh: </label>
                        <select
                          id={`refresh-${layer.name}`}
                          value={selectedRefreshIntervals.get(layer.name) || 5}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateRefreshInterval(layer.name, parseInt(e.target.value));
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value={0}>No auto-refresh</option>
                          <option value={1}>1 minute</option>
                          <option value={5}>5 minutes</option>
                          <option value={10}>10 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                        </select>
                      </div>

                      {/* CQL Filter */}
                      <div className="form-group-full">
                        <label htmlFor={`cql-filter-${layer.name}`}>CQL Filter (Optional):</label>
                        <input
                          id={`cql-filter-${layer.name}`}
                          type="text"
                          placeholder="e.g., status='active' AND temperature > 20"
                          value={selectedCQLFilters.get(layer.name) || ''}
                          onChange={(e) => {
                            e.stopPropagation();
                            updateCQLFilter(layer.name, e.target.value);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: '#666' }}>
                          Examples: status='active' | temp &gt; 20 | name LIKE '%fire%'
                        </small>
                      </div>
                    </>
                  );
                })()}
              </div>
            )}
            {/* Resource Type Indicator (for layers with only one type) */}
            {layer.resourceType && (!layer.availableTypes || layer.availableTypes.length === 1) && (
              <div className="layer-resource-type">
                Type: {layer.resourceType === 'featureType' ? 'Vector (WFS)' : 'Raster (WMS)'}
                {layer.geometryType && <span className="layer-geometry-hint"> ({layer.geometryType})</span>}
              </div>
            )}
            {/* Style Selector (only shown for WMS import) */}
            {layer.queryable && selectedImportTypes.get(layer.name) === 'wms' && layer.styles.length > 1 && (
              <div className="layer-styles">
                <label htmlFor={`style-${layer.name}`}>Style: </label>
                <select
                  id={`style-${layer.name}`}
                  value={selectedStyles.get(layer.name) || layer.styles[0]?.name || 'default'}
                  onChange={(e) => {
                    e.stopPropagation();
                    updateLayerStyle(layer.name, e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {layer.styles.map((style: any) => (
                    <option key={style.name} value={style.name}>
                      {style.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {layer.queryable && selectedImportTypes.get(layer.name) === 'wms' && layer.styles.length === 1 && (
              <div className="layer-styles">
                Style: {layer.styles[0].name}
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  return (
    <div className="modal-overlay import-geoserver-modal-overlay">
      <div className="modal-content import-geoserver-modal">
        <button onClick={() => onClose(false)} className="modal-close-btn">
          ×
        </button>

        <h2 className="modal-title">Import Layers from GeoServer</h2>

        {/* Loading State */}
        {loading && (
          <div className="loading-message">
            <p>Loading data from GeoServer <FontAwesomeIcon icon={faRotate} spin /></p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
            <button onClick={fetchLayers} className="btn btn-sm">
              Retry
            </button>
          </div>
        )}

        {/* Success State */}
        {successMessage && (
          <div className="success-message">
            <strong>✓</strong> {successMessage}
          </div>
        )}

        {/* Import Configuration */}
        {!loading && !error && layers.length > 0 && (
          <>
            <div className="import-config">
              <h3>Import Configuration</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Import as Category</label>
                  <select
                    value={importCategory}
                    onChange={(e) => setImportCategory(e.target.value as LayerCategory)}
                  >
                    <option value="backgrounds">Backgrounds</option>
                    <option value="overlays">Overlays</option>
                    <option value="live">Live Data</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Default Group</label>
                  <select
                    value={useCustomGroup ? '__custom__' : importGroup}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '__custom__') {
                        setUseCustomGroup(true);
                        setImportGroup('');
                      } else {
                        setUseCustomGroup(false);
                        setImportGroup(value);
                      }
                    }}
                  >
                    <option value="">Use workspace name</option>
                    {existingGroups.map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                    <option value="__custom__">Custom...</option>
                  </select>
                  {useCustomGroup && (
                    <input
                      type="text"
                      placeholder="Enter custom group name"
                      value={customGroupName}
                      onChange={(e) => setCustomGroupName(e.target.value)}
                      style={{ marginTop: '8px' }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Layer Selection */}
            <div className="layer-selection">
              <div className="layer-selection-header">
                <h3>
                  Available Layers ({filteredLayers.length}){' '}
                  <span className="selected-count">
                    {selectedLayers.size} selected
                  </span>
                </h3>

                <div className="layer-selection-actions">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="Filter layers..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                  />
                  <button onClick={selectAllVisible} className="btn btn-sm">
                    Select All
                  </button>
                  <button onClick={deselectAll} className="btn btn-sm">
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="layer-list">
                {filteredLayers.length === 0 ? (
                  <div className="no-layers-message">
                    <p>No layers found matching filter criteria</p>
                  </div>
                ) : (
                  // Only render top-level layers (no parent group)
                  filteredLayers
                    .filter(layer => !layer.parentGroup)
                    .map(layer => renderLayerItem(layer))
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="modal-actions">
              <button
                onClick={() => onClose(false)}
                className="btn btn-secondary"
                disabled={importing}
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                className="btn btn-primary"
                disabled={importing || selectedLayers.size === 0}
              >
                {importing
                  ? `Importing ${selectedLayers.size} layer(s)...`
                  : `Import ${selectedLayers.size} layer(s)`}
              </button>
            </div>
          </>
        )}

        {/* Empty State */}
        {!loading && !error && layers.length === 0 && (
          <div className="empty-state">
            <p>No layers found in GeoServer</p>
            <button onClick={fetchLayers} className="btn btn-primary">
              Reload
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
